import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, TextField, InputAdornment, Chip, Stack, MenuItem,
  ToggleButton, ToggleButtonGroup, CircularProgress, Alert,
  IconButton, Button, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PetsIcon from '@mui/icons-material/Pets';
import AddIcon from '@mui/icons-material/Add';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// Helper para calcular edad
function calcAge(birthDate) {
  if (!birthDate) return '—';
  const b = new Date(birthDate);
  const now = new Date();
  const years = Math.floor((now - b) / (365.25 * 864e5));
  if (years >= 1) return `${years} año${years !== 1 ? 's' : ''}`;
  const months = Math.floor((now - b) / (30.44 * 864e5));
  if (months >= 1) return `${months} mes${months !== 1 ? 'es' : ''}`;
  return '< 1 mes';
}

// Combina un paciente con su propietario en el shape que consumen los llamadores
function buildPatientRow(p, o = {}) {
  return {
    patientId:    p.id,
    id:            p.id,
    name:         p.name || '',
    species:      p.species || '',
    breed:        p.breed || '',
    genero:       p.genero || '',
    birthDate:    p.birthDate || '',
    ownerId:      p.ownerId || '',
    ownerName:    o.name     || '—',
    ownerRut:     o.rut      || '',
    ownerCelular: o.celular  || '',
    ownerMail:    o.mail || o.contact || '',
    ownerContact: o.mail || o.contact || '',
    ownerRegion:  o.region   || '',
    ownerComuna:  o.comuna   || '',
    ownerConvenio: o.convenio || '',
    photoURL:     p.photoURL || '',
    weight:       p.weight || '',
    microchip:    p.microchip || '',
    esterilizado: p.esterilizado || false,
    grupoSanguineo: p.grupoSanguineo || '',
    alergias:     p.alergias || '',
    condicionesCronicas: p.condicionesCronicas || '',
    isHospitalized: p.status === 'hospitalizado',
    estado_vital: p.estado_vital || 'VIVO',
    isDeceased:   p.estado_vital === 'FALLECIDO',
    status:       p.status || 'activo',
  };
}

export default function PatientSearch({
  onPatientSelect,
  selectedPatient,
  onNewPatient,
  showFilters = true,
  label = "Buscar paciente",
  // Permiten reutilizar datos ya cargados por la pantalla contenedora y evitar
  // volver a leer las colecciones completas de patients/owners.
  patients: patientsProp,
  owners: ownersProp,
}) {
  const [fetchedPatients, setFetchedPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [speciesFilter, setSpeciesFilter] = useState('Todos');
  const [sexFilter, setSexFilter] = useState('Todos');
  const [ageFilter, setAgeFilter] = useState('Todos');
  const [statusFilter, setStatusFilter] = useState('Todos');

  const hasProvidedData = Array.isArray(patientsProp) && patientsProp.length > 0;

  // Cargar pacientes solo si la pantalla contenedora no los entrego por props
  useEffect(() => {
    if (hasProvidedData) {
      setLoading(false);
      return;
    }
    const fetchPatients = async () => {
      setLoading(true);
      try {
        const [pSnap, oSnap] = await Promise.all([
          getDocs(collection(db, 'patients')),
          getDocs(collection(db, 'owners')),
        ]);
        const ownersMap = {};
        oSnap.forEach(d => { ownersMap[d.id] = { id: d.id, ...d.data() }; });
        setFetchedPatients(
          pSnap.docs.map(d => {
            const p = { id: d.id, ...d.data() };
            return buildPatientRow(p, ownersMap[p.ownerId] || {});
          })
        );
      } catch (err) {
        setError('Error al cargar los pacientes.');
      } finally {
        setLoading(false);
      }
    };
    fetchPatients();
  }, [hasProvidedData]);

  const patients = useMemo(() => {
    if (!hasProvidedData) return fetchedPatients;
    const ownersMap = {};
    for (const o of ownersProp || []) ownersMap[o.id] = o;
    return patientsProp.map((p) => buildPatientRow(p, ownersMap[p.ownerId] || {}));
  }, [hasProvidedData, patientsProp, ownersProp, fetchedPatients]);

  // Opciones de filtros
  const speciesOptions = useMemo(() => {
    const set = new Set(patients.map(p => p.species).filter(Boolean));
    return ['Todos', ...Array.from(set).sort()];
  }, [patients]);

  // Filtrado
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return patients.filter(p => {
      const matchSearch = !q ||
        p.name.toLowerCase().includes(q) ||
        p.ownerName.toLowerCase().includes(q) ||
        p.breed.toLowerCase().includes(q) ||
        (p.ownerRut || '').toLowerCase().includes(q) ||
        (p.microchip || '').toLowerCase().includes(q) ||
        (p.ownerCelular || '').includes(q) ||
        (p.ownerConvenio || '').toLowerCase().includes(q);

      const matchSpecies = speciesFilter === 'Todos' || p.species === speciesFilter;
      const matchSex = sexFilter === 'Todos' || p.genero === sexFilter;
      const matchVital = statusFilter === 'fallecidos'
        ? p.isDeceased
        : !p.isDeceased;
      const matchStatus = statusFilter === 'Todos'
        ? true
        : statusFilter === 'internados'
          ? p.isHospitalized
          : statusFilter === 'fallecidos'
            ? true
            : (p.status || 'activo') === statusFilter;

      const matchAge = ageFilter === 'Todos' || (() => {
        if (!p.birthDate) return false;
        const years = Math.floor((new Date() - new Date(p.birthDate)) / (365.25 * 864e5));
        if (ageFilter === 'cachorro') return years < 1;
        if (ageFilter === 'joven') return years >= 1 && years < 3;
        if (ageFilter === 'adulto') return years >= 3 && years < 8;
        if (ageFilter === 'senior') return years >= 8;
        return true;
      })();

      return matchSearch && matchSpecies && matchSex && matchVital && matchStatus && matchAge;
    });
  }, [patients, search, speciesFilter, sexFilter, statusFilter, ageFilter]);

  const handleClearFilters = () => {
    setSearch('');
    setSpeciesFilter('Todos');
    setSexFilter('Todos');
    setAgeFilter('Todos');
    setStatusFilter('Todos');
  };

  const hasActiveFilters = search || speciesFilter !== 'Todos' || sexFilter !== 'Todos' || 
                           ageFilter !== 'Todos' || statusFilter !== 'Todos';

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Buscador principal */}
      <TextField
        fullWidth
        size="small"
        placeholder="Buscar por nombre, propietario, RUT, microchip, teléfono, convenio…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: search && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearch('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: showFilters ? 2 : 1 }}
      />

      {/* Filtros */}
      {showFilters && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Chip
              label={`Especie: ${speciesFilter}`}
              size="small"
              onClick={() => setSpeciesFilter(speciesFilter === 'Todos' ? 'Canino' : 
                speciesFilter === 'Canino' ? 'Felino' : 'Todos')}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label={`Sexo: ${sexFilter}`}
              size="small"
              onClick={() => setSexFilter(sexFilter === 'Todos' ? 'Macho' : 
                sexFilter === 'Macho' ? 'Hembra' : 'Todos')}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label={`Edad: ${ageFilter}`}
              size="small"
              onClick={() => {
                const ages = ['Todos', 'cachorro', 'joven', 'adulto', 'senior'];
                const idx = ages.indexOf(ageFilter);
                setAgeFilter(ages[(idx + 1) % ages.length]);
              }}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label={`Estado: ${statusFilter}`}
              size="small"
              onClick={() => {
                const statuses = ['Todos', 'activo', 'inactivo', 'internados'];
                const idx = statuses.indexOf(statusFilter);
                setStatusFilter(statuses[(idx + 1) % statuses.length]);
              }}
              sx={{ cursor: 'pointer' }}
            />
            {hasActiveFilters && (
              <Chip
                label="Limpiar filtros"
                size="small"
                onDelete={handleClearFilters}
                color="primary"
                variant="outlined"
              />
            )}
          </Stack>
        </Box>
      )}

      {/* Lista de resultados */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {hasActiveFilters ? 'No se encontraron pacientes con los filtros actuales.' : 'No hay pacientes registrados.'}
          </Typography>
          {onNewPatient && search.trim() && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={onNewPatient}
              sx={{ mt: 1 }}
            >
              Crear nuevo paciente "{search}"
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: 2 }}>
          {filtered.map((patient) => (
            <Box
              key={patient.patientId}
              onClick={() => onPatientSelect(patient)}
              sx={{
                p: 2,
                borderBottom: '1px solid #F1F5F9',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#F8FAFC' },
                bgcolor: selectedPatient?.patientId === patient.patientId ? '#EFF6FF' : 'transparent',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PetsIcon sx={{ color: patient.species === 'Canino' ? '#FF9800' : '#AB47BC' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {patient.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {patient.species} · {patient.breed} · {patient.genero} · {calcAge(patient.birthDate)}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    Propietario: {patient.ownerName}
                    {patient.ownerRut && ` (${patient.ownerRut})`}
                  </Typography>
                </Box>
                {patient.isHospitalized && (
                  <Chip label="Internado" size="small" color="warning" />
                )}
                {patient.alergias && (
                  <Chip label="Alergias" size="small" color="error" />
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Contador */}
      {!loading && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {filtered.length} paciente{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}
        </Typography>
      )}
    </Box>
  );
}
